import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { registerBoardAlertHost } from '../utils/boardAlert';

/**
 * Hosts pretty confirm/alert/prompt dialogs for the canvas
 * (imperative boardAlert / boardConfirm / boardPrompt).
 */
const BoardAlertHost = () => {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(null);
  const [promptValue, setPromptValue] = useState('');
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);
  const inputRef = useRef(null);

  const settle = useCallback(result => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setBusy(false);
    setOpen(false);
    resolve?.(result);
  }, []);

  useEffect(() => {
    return registerBoardAlertHost(next => {
      if (resolverRef.current) {
        const prev = resolverRef.current;
        resolverRef.current = null;
        prev(next?.mode === 'prompt' ? null : false);
      }
      setRequest(next);
      setPromptValue(next?.mode === 'prompt' ? String(next.defaultValue || '') : '');
      setOpen(true);
      return new Promise(resolve => {
        resolverRef.current = resolve;
      });
    });
  }, []);

  const handleClose = () => {
    if (busy) return;
    if (request?.mode === 'alert') {
      settle(true);
      return;
    }
    settle(request?.mode === 'prompt' ? null : false);
  };

  const handleCancel = () => {
    if (busy) return;
    settle(request?.mode === 'prompt' ? null : false);
  };

  const handleConfirm = async () => {
    if (busy) return;
    if (request?.mode === 'prompt') {
      const value = String(promptValue || '').trim();
      if (request.required && !value) return;
      settle(value);
      return;
    }
    if (typeof request?.onConfirm === 'function') {
      setBusy(true);
      try {
        await request.onConfirm();
      } catch {
        /* errors are handled by callers */
      }
      settle(true);
      return;
    }
    settle(true);
  };

  if (!request) return null;

  const isConfirm = request.mode === 'confirm';
  const isPrompt = request.mode === 'prompt';
  const confirmColor = request.danger ? 'error' : 'primary';
  const confirmDisabled = isPrompt && request.required && !String(promptValue || '').trim();

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : handleClose}
      aria-labelledby="board-alert-title"
      aria-describedby="board-alert-message"
      className="board-alert-dialog"
      PaperProps={{ className: 'board-alert-paper' }}
      TransitionProps={{
        onExited: () => { setRequest(null); setBusy(false); },
        onEntered: () => {
          if (isPrompt) inputRef.current?.focus?.();
        },
      }}
    >
      <DialogTitle id="board-alert-title" className="board-alert-title">
        {request.title}
      </DialogTitle>
      <DialogContent className="board-alert-content">
        {request.message ? (
          <Typography id="board-alert-message" variant="body2" className="board-alert-message">
            {request.message}
          </Typography>
        ) : null}
        {request.href ? (
          <Box className="board-alert-link-row">
            <Link
              href={request.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              className="board-alert-link"
            >
              {request.hrefLabel || request.href}
            </Link>
          </Box>
        ) : null}
        {isPrompt ? (
          <TextField
            inputRef={inputRef}
            className="board-alert-prompt-field"
            label={request.label || undefined}
            placeholder={request.placeholder || ''}
            value={promptValue}
            onChange={e => setPromptValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
            }}
            fullWidth
            size="small"
            margin="dense"
            autoComplete="off"
          />
        ) : null}
      </DialogContent>
      <DialogActions className="board-alert-actions">
        {isConfirm || isPrompt ? (
          <Button onClick={handleCancel} color="inherit" className="board-alert-cancel-btn" disabled={busy}>
            {request.cancelLabel || 'Cancel'}
          </Button>
        ) : null}
        <Button
          onClick={handleConfirm}
          color={confirmColor}
          variant="contained"
          className="board-alert-confirm-btn"
          autoFocus={!isPrompt}
          disabled={confirmDisabled || busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {request.confirmLabel || 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardAlertHost;
